import React from 'react';
import Skeleton from './Skeleton';
// The Instruction Manual skeleton reuses the page's own card classes, so its CSS
// must be available before that page's lazy chunk has loaded.
import 'src/views/tutorial/InstructionManual.scss';

/**
 * Page-shaped skeletons (guide §15). Each one is built from the SAME layout and
 * card classes as the real page (.card-container, .row/.col-*, .template-card,
 * .training-module-card, .manual-topic-card …), so a placeholder card has the
 * real card's width and height and the page does not jump when data arrives.
 *
 * The grid parts are used twice: by the page itself while its data loads, and by
 * routeSkeletonFor() (wrapped in the page chrome) on a hard refresh while the
 * page's code is still loading — so both loading states look identical.
 */

const rnd = (i, lo, hi) => lo + ((i * 37) % (hi - lo));
const times = (n, fn) => Array.from({ length: n }).map((_, i) => fn(i));

const Lines = ({ count, height = 12, gap = 8, last = '60%', style }) => (
	<div className='ivs-sk-lines' style={{ gap, ...style }}>
		{times(count, (i) => (
			<Skeleton key={i} height={height} width={i === count - 1 ? last : '100%'} />
		))}
	</div>
);

/* ---------------- page chrome ---------------- */

// The standard page card: card-container > [row > col >] card > header-title header > body.
// `framed` = the Manage Users style (row.mt-10 > col around the card).
export const PageCardSkeleton = ({ title, framed = false, children }) => {
	const card = (
		<div className={framed ? 'card' : 'card app-card'}>
			<div className='card-header header-title'>{title}</div>
			<div className='card-body'>{children}</div>
		</div>
	);
	return (
		<div className='container-fluid card-container' role='status' aria-busy='true' aria-label='Loading'>
			{framed ? (
				<div className='row mt-10'>
					<div className='col'>{card}</div>
				</div>
			) : (
				card
			)}
		</div>
	);
};

// "Filter: [search] [select]…            [button]" row used above the card grids.
export const FilterBarSkeleton = ({ fields = 1, colClassName = 'col-md-8', action = 170 }) => (
	<div className='row mb-4 d-flex-center'>
		<div className={colClassName}>
			<div className='d-flex-center template-filter ivs-sk-filter'>
				<Skeleton width={44} height={14} />
				<Skeleton width={240} height={38} radius={8} />
				{times(fields - 1, (i) => (
					<Skeleton key={i} width={140} height={38} radius={8} />
				))}
			</div>
		</div>
		<div className='col'>
			{action ? (
				<div className='d-flex justify-content-end'>
					<Skeleton width={action} height={38} radius={8} />
				</div>
			) : null}
		</div>
	</div>
);

/* ---------------- card grids ---------------- */

// One placeholder card per grid cell, in the page's own row/col/card classes.
export const CardGridSkeleton = ({
	count = 6,
	rowClassName = 'row',
	colClassName = 'col-12 col-sm-6 col-md-4 mb-3',
	cardClassName = '',
	headerClassName = 'header-title',
	headerHeight,
	action = true,
	bodyClassName = '',
	bodyStyle,
	media = 250,
	children,
}) => (
	<div className={rowClassName} role='status' aria-busy='true' aria-label='Loading'>
		{times(count, (i) => (
			<div className={colClassName} key={i}>
				<div className={`card ${cardClassName}`.trim()}>
					<div className={`card-header ${headerClassName} ivs-sk-cardhead`.trim()} style={headerHeight ? { minHeight: headerHeight } : undefined}>
						<Skeleton width={`${45 + rnd(i, 5, 30)}%`} height={16} />
						{action ? <Skeleton width={28} height={28} radius={6} /> : null}
					</div>
					<div className={`card-body ${bodyClassName}`.trim()} style={bodyStyle}>
						{media ? <Skeleton height={media} radius={0} /> : null}
						{typeof children === 'function' ? children(i) : children}
					</div>
				</div>
			</div>
		))}
	</div>
);

// Phishing Material (/templates): 3 per row, header + preview image.
export const TemplateGridSkeleton = ({ count = 9 }) => (
	<CardGridSkeleton count={count} rowClassName='row template-grid' colClassName='col-12 col-sm-6 col-md-4' cardClassName='template-card' headerClassName='app-font' media={250} />
);

// Email / SMS templates and Phishing Websites: always 3 per row (col-4), preview + details.
export const SimpleTemplateGridSkeleton = ({ count = 9, headerClassName = '' }) => (
	<CardGridSkeleton count={count} colClassName='col-4 mb-3' headerClassName={headerClassName} bodyStyle={{ padding: 0 }} media={250}>
		<Lines count={3} style={{ padding: '1rem' }} />
	</CardGridSkeleton>
);

// Training Modules: header, 250px cover, duration + description (min 80px), action button.
export const TrainingGridSkeleton = ({ count = 9 }) => (
	<CardGridSkeleton count={count} rowClassName='row training-module-grid' colClassName='col-12 col-sm-6 col-md-4' cardClassName='training-module-card' bodyClassName='app-font' bodyStyle={{ padding: 0 }} media={250}>
		<div style={{ padding: '1rem' }}>
			<Skeleton width='45%' height={14} />
			<Lines count={3} height={11} style={{ minHeight: 80, paddingTop: 10 }} />
		</div>
		<div className='d-flex justify-content-end p-4'>
			<Skeleton width={64} height={31} radius={6} />
		</div>
	</CardGridSkeleton>
);

// News Feeds: 3 per row; fixed-height image and a 3-line summary (see .news-feed-*).
export const FeedGridSkeleton = ({ count = 6 }) => (
	<CardGridSkeleton count={count} colClassName='col-12 col-md-6 col-lg-4 mb-4' action={false} bodyClassName='app-font' bodyStyle={{ padding: 0 }} media={200}>
		<div className='news-feed-text'>
			<Lines count={3} height={12} gap={9} last='70%' style={{ minHeight: 'calc(3 * 1.5em)' }} />
			<div className='d-flex justify-content-end mt-3'>
				<Skeleton width={84} height={27} radius={6} />
			</div>
		</div>
	</CardGridSkeleton>
);

// System Notifiers: 3 per row (2 on md), header, preview, 4 detail lines.
export const SystemTemplateGridSkeleton = ({ count = 6 }) => (
	<CardGridSkeleton count={count} rowClassName='row g-3' colClassName='col-12 col-sm-6 col-md-6 col-lg-4 col-xl-4 mb-3' cardClassName='content-card' headerHeight={76} bodyStyle={{ padding: 0 }} media={250}>
		<Lines count={4} gap={14} style={{ padding: '1rem', minHeight: 139 }} />
	</CardGridSkeleton>
);

// Posters / Landing Page (admin): 3 per row, header with actions, 300px image.
export const PosterGridSkeleton = ({ count = 6 }) => (
	<CardGridSkeleton count={count} colClassName='col-12 col-sm-6 col-md-4 mb-4' headerHeight={71} bodyStyle={{ padding: 14 }} media={300} />
);

// Instruction Manual: the 8 guide cards, 2 per row, in the real .manual-* classes.
export const ManualTopicsSkeleton = ({ count = 8 }) => (
	<div className='row g-3' role='status' aria-busy='true' aria-label='Loading'>
		{times(count, (i) => (
			<div className='col-12 col-md-6 mb-2' key={i}>
				<div className='manual-topic-card'>
					<div>
						<div className='manual-card-top'>
							<Skeleton width={44} height={44} radius={10} />
							<Skeleton width={52} height={22} radius={6} />
						</div>
						<Skeleton width={`${40 + rnd(i, 5, 25)}%`} height={17} style={{ marginBottom: '0.6rem' }} />
						<Lines count={2} height={12} last={`${55 + rnd(i, 5, 30)}%`} style={{ marginBottom: '1rem' }} />
					</div>
					<div className='manual-card-action'>
						<Skeleton width={80} height={13} />
						<Skeleton width={16} height={16} radius={4} />
					</div>
				</div>
			</div>
		))}
	</div>
);

/* ---------------- full page skeletons (used by routeSkeletonFor) ---------------- */

export const InstructionManualPageSkeleton = () => (
	<PageCardSkeleton framed title={<strong>Instruction Manual</strong>}>
		<ManualTopicsSkeleton />
	</PageCardSkeleton>
);

export const NewsFeedsPageSkeleton = () => (
	<PageCardSkeleton framed title={<strong>News Feeds</strong>}>
		<FeedGridSkeleton />
	</PageCardSkeleton>
);

export const PhishingMaterialPageSkeleton = () => (
	<PageCardSkeleton title='Phishing Material'>
		<FilterBarSkeleton fields={5} colClassName='col-md-12' action={0} />
		<div className='row mb-4'>
			<div className='col d-flex justify-content-end'>
				<Skeleton width={190} height={38} radius={8} />
			</div>
		</div>
		<div className='ivs-sk-clip' style={{ maxHeight: 'calc(100vh - 278px)' }}>
			<TemplateGridSkeleton />
		</div>
	</PageCardSkeleton>
);

export const TrainingModulesPageSkeleton = () => (
	<PageCardSkeleton title='Training Modules'>
		<FilterBarSkeleton fields={3} action={130} />
		<TrainingGridSkeleton />
	</PageCardSkeleton>
);

export const SystemNotifiersPageSkeleton = () => (
	<PageCardSkeleton title='System Notifiers'>
		<SystemTemplateGridSkeleton />
	</PageCardSkeleton>
);

export const PosterPageSkeleton = ({ title }) => (
	<PageCardSkeleton title={title}>
		<FilterBarSkeleton fields={1} colClassName='col-md-6' action={140} />
		<PosterGridSkeleton />
	</PageCardSkeleton>
);

export const SimpleTemplatesPageSkeleton = ({ headerClassName }) => (
	<div className='container-fluid card-container' role='status' aria-busy='true' aria-label='Loading'>
		<FilterBarSkeleton fields={3} colClassName='col-md-6' />
		<SimpleTemplateGridSkeleton headerClassName={headerClassName} />
	</div>
);

// Profile form fields (also the in-page fallback while the form code loads).
export const ProfileFormSkeleton = ({ fields = 3 }) => (
	<div className='ivs-sk-lines' style={{ gap: 18 }}>
		{times(fields, (i) => (
			<div className='ivs-sk-field' key={i}>
				<Skeleton width={110} height={12} />
				<Skeleton width='100%' height={38} radius={8} />
			</div>
		))}
		<div className='d-flex justify-content-end'>
			<Skeleton width={130} height={38} radius={8} />
		</div>
	</div>
);

export const ProfilePageSkeleton = () => (
	<PageCardSkeleton framed title={<strong>Profile</strong>}>
		<div className='card app-card mb-3'>
			<div className='card-body d-flex flex-wrap align-items-center gap-3'>
				<div className='profile-img-container'>
					<Skeleton width={96} height={96} radius={999} />
					<Skeleton width='100%' height={38} radius={8} style={{ marginTop: 12 }} />
				</div>
				<div className='flex-grow-1 ivs-sk-lines' style={{ gap: 10 }}>
					<Skeleton width={220} height={26} />
					<Skeleton width={180} height={14} />
					<div className='d-flex gap-2'>
						<Skeleton width={80} height={20} radius={6} />
						<Skeleton width={56} height={20} radius={6} />
					</div>
				</div>
			</div>
		</div>
		<div className='row'>
			<div className='col-md-5 mb-3'>
				<div className='card app-card h-100'>
					<div className='card-header header-title'>Account Details</div>
					<div className='card-body'>
						{times(4, (i) => (
							<div className='d-flex mb-3' key={i}>
								<Skeleton width='35%' height={13} />
								<Skeleton width={`${30 + rnd(i, 5, 20)}%`} height={13} style={{ marginLeft: 'auto', marginRight: '10%' }} />
							</div>
						))}
					</div>
				</div>
			</div>
			<div className='col-md-7 mb-3'>
				<div className='card app-card h-100'>
					<div className='card-header header-title'>Edit Profile</div>
					<div className='card-body'>
						<ProfileFormSkeleton />
					</div>
				</div>
			</div>
		</div>
	</PageCardSkeleton>
);
